import * as Actions from './ImportEventsActions';

import { WizardPageEnum } from './interfaces/wizard-page';
import { StudyCoughEventType } from '../../misc/api/apiTypes';
import ImportEventsAnalysisSet from './viewmodels/ImportEventsAnalysisSet';

export enum ImportStatus {
    NotImported,
    Importing,
    Imported,
    FailedImport
}

export interface ImportEvent {
    Id: number;
    Position: number;
    IsImporting: ImportStatus;
}

export interface ImportEventsState {
    isVisible: boolean;
    studyId: string;
    currentPage: WizardPageEnum;
    analysisSetId: string;
    analysisSet: ImportEventsAnalysisSet;
    hourlySegment: number;
    eventsFileName: string;
    eventsList: Array<ImportEvent>;
    nextAction: any;
    backAction: any;
    isErrorVisible: boolean;
    coughEventDetails: StudyCoughEventType;
}

export const initialImportEvents: ImportEventsState = {
    isVisible: false,
    studyId: null,
    currentPage: WizardPageEnum.FileSelection,
    analysisSetId: null,
    analysisSet: null,
    hourlySegment: null,
    eventsFileName: null,
    eventsList: [],
    nextAction: Actions.JumpToConfirmationAction,
    backAction: Actions.CancelAction,
    isErrorVisible: false,
    coughEventDetails: null
}


/**
 * 
 * Updating the event list for a single event that has started importing, finished importing or has encountered an error when importing.
 * 
 * @param eventsList
 * @param action
 * @param importStatus
 */
const UpdateEventList = (eventsList: Array<ImportEvent>, action: Actions.StartImportEventAction | Actions.FinishImportEventAction | Actions.ErrorImportingEventAction, importStatus: ImportStatus): Array<ImportEvent> => {
    const updatedItems = eventsList.map(item => {
        if (item.Id === action.eventValue.Id) {
            return { ...item, IsImporting: importStatus };
        }
        return item;
    });

    return updatedItems
}

const StartImportEvent = (state: ImportEventsState, action: Actions.StartImportEventAction): ImportEventsState => {
    let updatedItems = UpdateEventList(state.eventsList, action, ImportStatus.Importing);

    return {
        ...state,
        eventsList: updatedItems
    };
}

/**
 * 
 * Finishing the import of an event. Note the IsImporting field is the import status that is being switched to.
 * 
 * @param state
 * @param action
 */
const FinishImportEvent = (state: ImportEventsState, action: Actions.FinishImportEventAction): ImportEventsState => {
    let updatedItems = UpdateEventList(state.eventsList, action, ImportStatus.Imported);

    return {
        ...state,
        eventsList: updatedItems
    }
}

/**
 * 
 * Marking the event as a failed import. Used when there is an error returned from the server API.
 * 
 * @param state
 * @param action
 */
const MarkEventError = (state: ImportEventsState, action: Actions.ErrorImportingEventAction): ImportEventsState => {
    let updatedItems = UpdateEventList(state.eventsList, action, ImportStatus.FailedImport);

    return {
        ...state,
        eventsList: updatedItems
    }
}

export function importEventsReducer(state: ImportEventsState = null, action: Actions.Action): ImportEventsState {
    switch (action.type) {
        case Actions.ActionType.CloseImportEventsDialog:
            //When the dialog closes, the state should be reset or
            //the user would have to refresh the page.
            return {
                ...initialImportEvents,
                isVisible: false
            };
        case Actions.ActionType.SelectAnalysisSet:
            return {
                ...state,
                analysisSetId: action.analysisSetId
            };
        case Actions.ActionType.SetEventsFile:
            return {
                ...state,
                eventsFileName: action.filename
            };
        case Actions.ActionType.JumpToFileSelection:
            return {
                ...state,
                currentPage: action.currentPage,
                nextAction: action.nextAction,
                backAction: action.backAction,
            };
        case Actions.ActionType.JumpToConfirmation:
            return {
                ...state,
                currentPage: action.currentPage,
                nextAction: action.nextAction,
                backAction: action.backAction,
            };
        case Actions.ActionType.JumpToImporting:
            return {
                ...state,
                currentPage: action.currentPage,
                nextAction: action.nextAction,
                backAction: action.backAction,
            };
        case Actions.ActionType.JumpToImportSummary:
            return {
                ...state,
                currentPage: action.currentPage,
                nextAction: action.nextAction,
                backAction: action.backAction,
            };
        case Actions.ActionType.JumpToFinish:
            return {
                ...state,
                currentPage: action.currentPage
            };
        case Actions.ActionType.Cancel:
            return {
                ...state,
                currentPage: action.currentPage
            };
        case Actions.ActionType.StartImportEvent:
            return StartImportEvent(state, action);        
        case Actions.ActionType.FinishImportEvent:
            return FinishImportEvent(state, action);
        case Actions.ActionType.ErrorImportingEvent:
            return MarkEventError(state, action);
        case Actions.ActionType.SetEventsList:
            return {
                ...state,
                eventsList: action.eventsList
            };
        default:
            return state;
    }
}
